import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { productAPI, quoteAPI } from '../services/api';

const RequestQuote = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('product');

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (productId) {
      fetchProduct();
    } else {
      setError('No product selected');
      setLoading(false);
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await productAPI.getProductById(productId);
      if (response.data.success) {
        setProduct(response.data.data);
        formik.setFieldValue('unit', response.data.data.unit || '');
      }
    } catch (err) {
      setError('Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      quantity: '',
      unit: '',
      customer_notes: ''
    },
    validationSchema: Yup.object({
      quantity: Yup.number()
        .min(0.01, 'Quantity must be greater than 0')
        .required('Quantity is required'),
      unit: Yup.string(),
      customer_notes: Yup.string()
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      setError('');
      setSuccess('');

      try {
        const response = await quoteAPI.createQuote({
          product_id: parseInt(productId),
          quantity: parseFloat(values.quantity),
          unit: values.unit,
          customer_notes: values.customer_notes
        });

        if (response.data.success) {
          setSuccess('Quote request submitted successfully!');
          setTimeout(() => {
            navigate('/quotes');
          }, 2000);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to submit quote request');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !product) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate('/products')} sx={{ mt: 2 }}>
            Back to Products
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            Loha Mart
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate('/products')}>
            Products
          </Button>
          <Button color="inherit" onClick={() => navigate('/quotes')}>
            My Quotes
          </Button>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md">
        <Box py={4}>
          <Typography variant="h4" gutterBottom>
            Request Quote
          </Typography>

          {product && (
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Chip
                    label={product.category?.name || 'Uncategorized'}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Typography variant="h6" gutterBottom>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {product.description}
                </Typography>
                {product.price_range_min && product.price_range_max && (
                  <Typography variant="body1" color="primary" sx={{ mt: 2 }}>
                    Indicative Price: ₹{product.price_range_min} - ₹{product.price_range_max}
                    {product.unit && ` / ${product.unit}`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          <Paper elevation={3} sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="quantity"
                    name="quantity"
                    label="Quantity *"
                    type="number"
                    inputProps={{ step: "0.01", min: "0.01" }}
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                    helperText={formik.touched.quantity && formik.errors.quantity}
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="unit"
                    name="unit"
                    label="Unit"
                    placeholder="e.g., kg, ton, piece"
                    value={formik.values.unit}
                    onChange={formik.handleChange}
                    error={formik.touched.unit && Boolean(formik.errors.unit)}
                    helperText={formik.touched.unit && formik.errors.unit}
                    disabled={submitting}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="customer_notes"
                    name="customer_notes"
                    label="Additional Notes / Requirements"
                    multiline
                    rows={4}
                    placeholder="Please specify any additional requirements, delivery location, or special instructions..."
                    value={formik.values.customer_notes}
                    onChange={formik.handleChange}
                    error={formik.touched.customer_notes && Boolean(formik.errors.customer_notes)}
                    helperText={formik.touched.customer_notes && formik.errors.customer_notes}
                    disabled={submitting}
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/products/${productId}`)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{ flexGrow: 1 }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit Quote Request'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default RequestQuote;
